import { Injectable, Signal, inject } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { Event, NavigationEnd, Router } from "@angular/router";
import { getLanguageValues } from "@utils";
import { filter, map, scan } from "rxjs";

// Routes with no Futures-specific equivalent (same page/menu entry regardless of mode) --
// entering one of these must not change isFuturesRoute, it should keep whatever mode the
// user was already in. Keep this in sync with the shared entries in MENU_ITEMS/FUTURES_MENU_ITEMS.
// "download", "trade2earn" and "affiliate" each have their own futures/... route (FuturesDownloadsComponent,
// FuturesEarnComponent, FuturesAffiliateComponent) so none is neutral -- they must resolve via the "futures" firstSegment
// check below like any other futures-specific route.
const NEUTRAL_ROUTE_SEGMENTS: string[] = ["profile", "trading-academy", "contact-us", "billing"];

@Injectable({
  providedIn: "root",
})
export class FuturesModeService {
  // Declared before isFuturesRoute, whose initializer uses it.
  private readonly _router: Router = inject(Router);

  private readonly _initialMode: boolean = this._resolveFuturesMode(this._router.url, false);

  public readonly isFuturesRoute: Signal<boolean> = toSignal(
    this._router.events.pipe(
      filter((event: Event): event is NavigationEnd => event instanceof NavigationEnd),
      map((event: NavigationEnd) => event.urlAfterRedirects),
      scan(
        (previous: boolean, url: string) => this._resolveFuturesMode(url, previous),
        this._initialMode,
      ),
    ),
    { initialValue: this._initialMode },
  );

  private _resolveFuturesMode(url: string, previous: boolean): boolean {
    const path: string = url.split(/[?#]/)[0];
    const segments: string[] = path.split("/").filter((segment: string): boolean => segment !== "");
    const langSegmentCount: number = getLanguageValues().includes(segments[0]) ? 1 : 0;
    const firstSegment: string | undefined = segments[langSegmentCount];

    if (firstSegment === "futures") {
      return true;
    }

    if (firstSegment !== undefined && NEUTRAL_ROUTE_SEGMENTS.includes(firstSegment)) {
      return previous;
    }

    return false;
  }
}
